import { z } from "zod";

/**
 * Adresboek (docs/ADRESBOEK.md): bedrijven en personen buiten de eigen gebruikers, zoals leveranciers en
 * huurders. Contactpersonen van klanten staan apart (clients.ts).
 */

export const COMPANY_KINDS = ["supplier", "tenant", "other"] as const;
export type CompanyKind = (typeof COMPANY_KINDS)[number];
export const COMPANY_KIND_LABELS: Record<CompanyKind, string> = {
  supplier: "Leverancier",
  tenant: "Huurder",
  other: "Overig",
};

/** Vaste lijst vakgebieden, zodat filteren en de keuzelijst in de planning werken. */
export const TRADES = [
  "Brandveiligheid",
  "Liften",
  "Installaties W",
  "Installaties E",
  "Dak en gevel",
  "Schoonmaak",
  "Groen",
  "Beveiliging",
  "Overig",
] as const;
export type Trade = (typeof TRADES)[number];

/** Naam van het bedrijf dat elke organisatie heeft: de eigen medewerkers. */
export const INTERNAL_COMPANY_NAME = "Eigen dienst";

/** Een erkenning die binnen zoveel dagen verloopt, krijgt een waarschuwing. */
export const CERT_WARN_DAYS = 60;

export const WEEKDAYS = ["ma", "di", "wo", "do", "vr", "za", "zo"] as const;
export type Weekday = (typeof WEEKDAYS)[number];
export const WEEKDAY_LABELS: Record<Weekday, string> = {
  ma: "Maandag",
  di: "Dinsdag",
  wo: "Woensdag",
  do: "Donderdag",
  vr: "Vrijdag",
  za: "Zaterdag",
  zo: "Zondag",
};

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Gebruik uu:mm");
const dayHours = z
  .object({ from: hhmm, to: hhmm })
  .refine((d) => d.from < d.to, { message: "Van moet voor tot liggen" })
  .nullable();
/** Openingstijden per weekdag; null of ontbrekend = gesloten. */
export const openingHoursSchema = z.partialRecord(z.enum(WEEKDAYS), dayHours);
export type OpeningHours = z.output<typeof openingHoursSchema>;

const trimmed = (max: number) => z.string().trim().max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null));
const optionalEmail = z
  .email("Vul een geldig e-mailadres in")
  .trim()
  .toLowerCase()
  .nullish()
  .or(z.literal("").transform(() => null))
  .transform((v) => v ?? null);

const companyFields = z.object({
  name: trimmed(120).min(1, "Vul een naam in"),
  kinds: z.array(z.enum(COMPANY_KINDS)).min(1, "Kies minstens één soort").max(3),
  trades: z.array(z.enum(TRADES)).max(TRADES.length).default([]),
  kvk: z
    .string()
    .trim()
    .regex(/^\d{8}$/, "Een KvK-nummer heeft 8 cijfers")
    .nullish()
    .or(z.literal("").transform(() => null))
    .transform((v) => v ?? null),
  address: optionalText(200),
  postalCode: optionalText(10),
  city: optionalText(80),
  email: optionalEmail,
  phone: optionalText(30),
  emergencyPhone: optionalText(30),
  openingHours: openingHoursSchema.nullish().transform((v) => v ?? null),
  website: optionalText(200),
  notes: optionalText(2000),
});
export const companyCreateSchema = companyFields.extend({ id: z.uuid().optional() });
export const companyUpdateSchema = companyFields.partial();
export type CompanyCreateBody = z.input<typeof companyCreateSchema>;
export type CompanyUpdateBody = z.input<typeof companyUpdateSchema>;

const isoDate = z.iso.date("Kies een geldige datum");
const certFields = z.object({
  name: trimmed(120).min(1, "Vul een naam in"),
  number: optionalText(60),
  validUntil: isoDate.nullish().transform((v) => v ?? null),
});
export const certCreateSchema = certFields.extend({ id: z.uuid().optional() });
export const certUpdateSchema = certFields.partial();
export type CertCreateBody = z.input<typeof certCreateSchema>;

const personFields = z.object({
  name: trimmed(120).min(1, "Vul een naam in"),
  companyId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  jobTitle: optionalText(80),
  email: optionalEmail,
  phone: optionalText(30),
  mobile: optionalText(30),
  availability: optionalText(120),
  notes: optionalText(2000),
});
/** Met `userId` (eigen dienst): naam en mailadres komen uit het account als ze leeg zijn. */
export const personCreateSchema = personFields
  .extend({ id: z.uuid().optional(), userId: z.uuid().optional(), name: trimmed(120).default("") })
  .refine((p) => p.name || p.userId, { message: "Vul een naam in", path: ["name"] });
export const personUpdateSchema = personFields.partial();
export type PersonCreateBody = z.input<typeof personCreateSchema>;
export type PersonUpdateBody = z.input<typeof personUpdateSchema>;

export const personLinkUserSchema = z.object({ userId: z.uuid() });
export const companyMergeSchema = z.object({ intoId: z.uuid() });

/** Zoeken naar dubbelen bij een nieuw of gewijzigd record. */
export const duplicateQuerySchema = z.object({
  type: z.enum(["company", "person"]),
  name: z.string().trim().max(120).optional(),
  kvk: z.string().trim().max(20).optional(),
  email: z.string().trim().max(200).optional(),
  phone: z.string().trim().max(30).optional(),
  /** Het record zelf (bij bewerken) niet meetellen. */
  exclude: z.uuid().optional(),
});
export type DuplicateQuery = z.input<typeof duplicateQuerySchema>;

export const CERT_STATES = ["ok", "warn", "expired", "unknown"] as const;
export type CertState = (typeof CERT_STATES)[number];
export const CERT_STATE_LABELS: Record<CertState, string> = {
  ok: "Geldig",
  warn: "Verloopt binnenkort",
  expired: "Verlopen",
  unknown: "Geen datum",
};

export interface CertificationDto {
  id: string;
  companyId: string;
  name: string;
  number: string | null;
  validUntil: string | null;
  state: CertState;
}

export interface CompanyDto {
  id: string;
  name: string;
  kinds: CompanyKind[];
  trades: Trade[];
  kvk: string | null;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  email: string | null;
  phone: string | null;
  emergencyPhone: string | null;
  openingHours: OpeningHours | null;
  website: string | null;
  notes: string | null;
  /** Eigen dienst: bestaat altijd, niet te archiveren of samen te voegen. */
  isInternal: boolean;
  archivedAt: string | null;
  personCount: number;
  /** Slechtste stand van de erkenningen; null zonder erkenningen. */
  certState: CertState | null;
}

export interface DirectoryPersonDto {
  id: string;
  name: string;
  company: { id: string; name: string; isInternal: boolean } | null;
  jobTitle: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  availability: string | null;
  notes: string | null;
  user: { id: string; name: string | null; email: string } | null;
  /** Gebruiker met hetzelfde mailadres; de beheerder bevestigt de koppeling. Nooit automatisch. */
  userSuggestion: { id: string; name: string | null; email: string } | null;
  archivedAt: string | null;
  /** Persoonsgegevens gewist (AVG); de verwijzingen blijven bestaan. */
  erased: boolean;
  /** Alleen bij één persoon ophalen: koppelingen aan gebouwen die de gebruiker ziet. */
  links?: ContactLinkDto[];
}

export interface CompanyDetailDto extends CompanyDto {
  people: DirectoryPersonDto[];
  certifications: CertificationDto[];
  /** Koppelingen aan gebouwen die de gebruiker ziet (stap 2). */
  links: ContactLinkDto[];
}

export interface DuplicateDto {
  type: "company" | "person";
  id: string;
  name: string;
  /** Waarop het lijkt: `kvk`, `email`, `phone` of `name`. */
  match: "kvk" | "email" | "phone" | "name";
  detail: string | null;
}

export interface DirectoryUserDto {
  id: string;
  name: string | null;
  email: string;
  /** Al gekoppeld aan deze persoon in het adresboek. */
  personId: string | null;
}

/**
 * Telefoonnummer zonder opmaak, voor de dubbelcheck: `010-1234567`, `+31 10 123 45 67` en
 * `0031101234567` worden allemaal `0101234567`. Korter dan 6 cijfers telt niet.
 */
export function normalizePhone(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const plus = raw.trim().startsWith("+");
  let d = raw.replace(/\D/g, "");
  if (plus) d = `00${d}`;
  // Nederlands nummer in internationale notatie, ook met (0): +31 (0)10 ...
  if (d.startsWith("0031")) d = `0${d.slice(4).replace(/^0/, "")}`;
  return d.length >= 6 ? d : null;
}

/** Naam om op te vergelijken: kleine letters, zonder rechtsvorm en leestekens. */
export function normalizeName(raw: string): string {
  return raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\b(b\.?v\.?|n\.?v\.?|v\.?o\.?f\.?|vof|bv|nv|holding|groep)\b/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

const daysBetween = (fromIso: string, toIso: string) =>
  Math.round((Date.parse(`${toIso}T00:00:00Z`) - Date.parse(`${fromIso}T00:00:00Z`)) / 86_400_000);

/** Stand van een erkenning op `today` (YYYY-MM-DD). */
export function certState(validUntil: string | null, today: string): CertState {
  if (!validUntil) return "unknown";
  const d = daysBetween(today, validUntil);
  if (d < 0) return "expired";
  return d <= CERT_WARN_DAYS ? "warn" : "ok";
}

const CERT_RANK: Record<CertState, number> = { ok: 0, unknown: 1, warn: 2, expired: 3 };
/** Slechtste stand van een lijst erkenningen; null zonder erkenningen. */
export function worstCertState(states: CertState[]): CertState | null {
  return states.reduce<CertState | null>((w, s) => (w === null || CERT_RANK[s] > CERT_RANK[w] ? s : w), null);
}

/** Weekdag (ma–zo) en tijd (uu:mm) in Nederland. */
export function amsterdamClock(now: Date): { day: Weekday; time: string } {
  const parts = new Intl.DateTimeFormat("nl-NL", {
    timeZone: "Europe/Amsterdam",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  const day = get("weekday").slice(0, 2).toLowerCase() as Weekday;
  return { day, time: `${get("hour").padStart(2, "0")}:${get("minute")}` };
}

/** Open op dit moment? null als er geen openingstijden zijn vastgelegd. */
export function isOpenNow(hours: OpeningHours | null | undefined, now: Date): boolean | null {
  if (!hours || !Object.values(hours).some(Boolean)) return null;
  const { day, time } = amsterdamClock(now);
  const h = hours[day];
  return !!h && h.from <= time && time < h.to;
}

/** Openingstijden als korte tekst, bijv. "ma–vr 07:00–17:00, za 08:00–12:00". */
export function formatOpeningHours(hours: OpeningHours | null | undefined): string {
  if (!hours) return "";
  const out: string[] = [];
  let i = 0;
  while (i < WEEKDAYS.length) {
    const h = hours[WEEKDAYS[i]!];
    if (!h) {
      i++;
      continue;
    }
    let j = i;
    while (j + 1 < WEEKDAYS.length) {
      const n = hours[WEEKDAYS[j + 1]!];
      if (!n || n.from !== h.from || n.to !== h.to) break;
      j++;
    }
    const days = j === i ? WEEKDAYS[i] : `${WEEKDAYS[i]}–${WEEKDAYS[j]}`;
    out.push(`${days} ${h.from}–${h.to}`);
    i = j + 1;
  }
  return out.join(", ");
}

// ---------- koppelingen aan gebouw, verdieping en ruimte (stap 2) ----------

export const CONTACT_LINK_ROLES = ["tenant", "contact", "key_holder", "emergency", "supplier"] as const;
export type ContactLinkRole = (typeof CONTACT_LINK_ROLES)[number];
export const CONTACT_LINK_ROLE_LABELS: Record<ContactLinkRole, string> = {
  tenant: "Huurder",
  contact: "Contactpersoon",
  key_holder: "Sleutelhouder",
  emergency: "Noodcontact",
  supplier: "Vaste leverancier",
};

const linkFields = z.object({
  floorId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  roomId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  role: z.enum(CONTACT_LINK_ROLES),
  /** Bij een vaste leverancier: waarvoor (bijv. Liften). */
  trade: z
    .enum(TRADES)
    .nullish()
    .transform((v) => v ?? null),
  validFrom: isoDate,
  validTo: isoDate.nullish().transform((v) => v ?? null),
  isPrimary: z.boolean().default(false),
  notes: optionalText(500),
});
const periodOk = (l: { validFrom?: string; validTo?: string | null }) =>
  !l.validFrom || !l.validTo || l.validTo >= l.validFrom;
const periodMsg = { message: "De einddatum ligt voor de begindatum", path: ["validTo"] };

/** Een bedrijf óf een persoon aan een gebouw (en eventueel verdieping of ruimte) koppelen. */
export const contactLinkCreateSchema = linkFields
  .extend({
    id: z.uuid().optional(),
    companyId: z
      .uuid()
      .nullish()
      .transform((v) => v ?? null),
    personId: z
      .uuid()
      .nullish()
      .transform((v) => v ?? null),
  })
  .refine((l) => !!l.companyId !== !!l.personId, { message: "Kies een bedrijf of een persoon", path: ["companyId"] })
  .refine(periodOk, periodMsg);
export const contactLinkUpdateSchema = linkFields.partial().refine(periodOk, periodMsg);
export type ContactLinkCreateBody = z.input<typeof contactLinkCreateSchema>;
export type ContactLinkUpdateBody = z.input<typeof contactLinkUpdateSchema>;

/** Loopt de koppeling op `today`? Een einddatum telt nog mee (tot en met). */
export const isLinkCurrent = (l: { validFrom: string; validTo: string | null }, today: string) =>
  l.validFrom <= today && (!l.validTo || l.validTo >= today);

export interface ContactLinkDto {
  id: string;
  role: ContactLinkRole;
  trade: Trade | null;
  company: {
    id: string;
    name: string;
    kinds: CompanyKind[];
    phone: string | null;
    emergencyPhone: string | null;
    email: string | null;
    openingHours: OpeningHours | null;
  } | null;
  person: {
    id: string;
    name: string;
    jobTitle: string | null;
    companyName: string | null;
    phone: string | null;
    mobile: string | null;
    email: string | null;
    availability: string | null;
  } | null;
  building: { id: string; code: string; name: string };
  floor: { id: string; name: string } | null;
  room: { id: string; name: string } | null;
  validFrom: string;
  validTo: string | null;
  /** Loopt vandaag (niet in de toekomst, niet afgelopen). */
  current: boolean;
  isPrimary: boolean;
  notes: string | null;
}

/** Tab Contacten van een gebouw: de klant met zijn contactpersonen (alleen lezen) en de koppelingen. */
export interface BuildingContactsDto {
  client: {
    id: string;
    name: string;
    isUnassigned: boolean;
    contacts: {
      id: string;
      name: string;
      jobTitle: string | null;
      email: string | null;
      phone: string | null;
      isPrimary: boolean;
    }[];
  };
  links: ContactLinkDto[];
}

// ---------- keuzelijst en bestaande vrije tekst (stap 3) ----------

/** Keuzelijst voor andere modules (planning, taken, keuringen): bedrijven met hun personen. */
export const pickQuerySchema = z.object({
  kind: z.enum(COMPANY_KINDS).optional(),
  trade: z.enum(TRADES).optional(),
  /** Vaste leveranciers van dit gebouw komen bovenaan. */
  buildingId: z.uuid().optional(),
  q: z.string().trim().max(120).optional(),
});
export type PickQuery = z.input<typeof pickQuerySchema>;

export interface PickCompanyDto {
  id: string;
  name: string;
  kinds: CompanyKind[];
  trades: Trade[];
  phone: string | null;
  emergencyPhone: string | null;
  isInternal: boolean;
  certState: CertState | null;
  /** Lopende koppeling als vaste leverancier bij het gevraagde gebouw. */
  linked: boolean;
  people: { id: string; name: string; jobTitle: string | null; phone: string | null; mobile: string | null }[];
}

/** Naam uit vrije tekst (`assignee_text` van taken, `performer` van keuringen) die nog niet in het adresboek staat. */
export interface LegacyNameDto {
  text: string;
  tasks: number;
  obligations: number;
  /** Bestaand bedrijf met (bijna) dezelfde naam, als voorstel. */
  match: { id: string; name: string } | null;
}

export const LEGACY_ACTIONS = ["create", "link", "ignore"] as const;
export const legacyResolveSchema = z
  .object({
    text: z.string().trim().min(1).max(200),
    action: z.enum(LEGACY_ACTIONS),
    companyId: z.uuid().optional(),
  })
  .refine((b) => b.action !== "link" || !!b.companyId, { message: "Kies een bedrijf", path: ["companyId"] });
export type LegacyResolveBody = z.input<typeof legacyResolveSchema>;

// ---------- bedrijfspagina (premium adresboek) ----------

/** Kengetallen per bedrijf voor de lijst: gekoppelde gebouwen en werk dat (te laat) open staat. */
export interface CompanyStatsDto {
  companyId: string;
  /** Gebouwen met een lopende koppeling (die de gebruiker ziet). */
  buildings: number;
  /** Open taken en uitvoeringen die binnen 30 dagen of eerder moeten. */
  openWork: number;
  lateWork: number;
}

/** Soort werk op de bedrijfspagina: taak (ook storing), geplande uitvoering of vastgelegde keuring/onderhoud. */
export const COMPANY_WORK_KINDS = ["task", "obligation", "record"] as const;
export type CompanyWorkKind = (typeof COMPANY_WORK_KINDS)[number];

/** Stand van een werkregel: open, bezig, te laat, gepland (uitvoering nog niet aan de beurt) of klaar. */
export const COMPANY_WORK_STATES = ["open", "bezig", "late", "planned", "done"] as const;
export type CompanyWorkState = (typeof COMPANY_WORK_STATES)[number];
export const COMPANY_WORK_STATE_LABELS: Record<CompanyWorkState, string> = {
  open: "Open",
  bezig: "Bezig",
  late: "Te laat",
  planned: "Gepland",
  done: "Klaar",
};

export interface CompanyWorkItemDto {
  kind: CompanyWorkKind;
  id: string;
  /** Taakcode (`W455-T0003`); leeg bij uitvoeringen. */
  code: string | null;
  title: string;
  /** Soort in woorden: "Taak", "Storing", "Keuring", "Onderhoud", … */
  type: string;
  building: { id: string; code: string; name: string };
  asset: { id: string; name: string } | null;
  state: CompanyWorkState;
  /** Deadline of volgende keer (open), of datum van uitvoeren/afronden (klaar). */
  date: string | null;
  /** Resultaat van een keuring (alleen `record`). */
  result: "goedgekeurd" | "met_opmerkingen" | "afgekeurd" | null;
  /** Certificaat of werkbon (alleen `record`). */
  fileId: string | null;
  /** Valt onder een contractpost. */
  contract: { id: string; name: string } | null;
  /** Aantal bevindingen in de taak die nog niet opgelost zijn. */
  openIssues: number;
}

export interface CompanyWorkDto {
  open: CompanyWorkItemDto[];
  /** Afgerond werk van de laatste 24 maanden, nieuwste eerst. */
  done: CompanyWorkItemDto[];
}

/** Herkomst van een document op de bedrijfspagina. */
export const COMPANY_DOC_SOURCES = ["certificate", "contract", "dossier"] as const;
export type CompanyDocSource = (typeof COMPANY_DOC_SOURCES)[number];
export const COMPANY_DOC_SOURCE_LABELS: Record<CompanyDocSource, string> = {
  certificate: "Keuringscertificaten en werkbonnen",
  contract: "Contracten",
  dossier: "Gebouwdossier",
};

export interface CompanyDocumentDto {
  source: CompanyDocSource;
  /** Document (dossier/contract) of keuring (certificaat). */
  id: string;
  title: string;
  /** Meta-informatie in één regel, bijvoorbeeld de datum en het gebouw. */
  detail: string;
  date: string | null;
  building: { id: string; code: string; name: string } | null;
  /** Het bestand om te openen; leeg als er (nog) geen bestand is. */
  fileId: string | null;
  mime: string | null;
  /** Document in het dossier om naartoe te gaan. */
  documentId: string | null;
  /** Vervaldatum (dossier) of volgende keuring. */
  validUntil: string | null;
}

/** Periode op Bedrijfspagina › Inzichten: laatste 12 maanden, een kalenderjaar ("2025") of alles (per jaar). */
export const insightPeriodSchema = z.union([z.literal("12m"), z.literal("all"), z.string().regex(/^20\d\d$/)]);
export type InsightPeriod = z.infer<typeof insightPeriodSchema>;

/** Eén staaf of punt in de grafieken: een maand ("2026-03") of bij *Alles* een jaar ("2025"). */
export interface InsightBucketDto {
  key: string;
  label: string;
  contractCents: number;
  otherCents: number;
  jobs: number;
  /** Mediaan van gemeld tot begonnen, in minuten; null zonder gestarte opdrachten. */
  responseMinutes: number | null;
}

/** Kengetallen van één bedrijf over een periode (ook gebruikt in de vergelijking). */
export interface InsightMetricsDto {
  jobs: number;
  breakdowns: number;
  tasks: number;
  visits: number;
  /** Null als je geen bedragen mag zien (module Contracten uit of rol Klant). */
  contractCents: number | null;
  otherCents: number | null;
  responseMinutes: number | null;
  /** Reactietijd gaat alleen over storingen (er zijn storingen onder een contract); anders over alle taken. */
  responseOfBreakdowns: boolean;
  /** Kortste afgesproken responstijd uit de storingsposten van de contracten. */
  slaHours: number | null;
  /** Aandeel storingen met afspraak dat binnen de afspraak begon (0–100). */
  withinSlaPct: number | null;
  /** Aandeel taken met een deadline dat op tijd klaar was (0–100). */
  onTimePct: number | null;
  /** Mediaan van gemeld tot klaar, in dagen. */
  leadDays: number | null;
  inspections: number;
  /** Keuringen zonder afkeur (0–100). */
  passPct: number | null;
  repeats: number;
}

export interface CompanyInsightsDto {
  period: InsightPeriod;
  from: string;
  to: string;
  /** Jaren met gegevens, nieuwste eerst (keuze in de periodebalk). */
  years: string[];
  buckets: InsightBucketDto[];
  metrics: InsightMetricsDto;
  perBuilding: { building: { id: string; code: string; name: string }; jobs: number; costCents: number | null }[];
  repeats: {
    asset: { id: string; name: string };
    building: { id: string; code: string; name: string };
    count: number;
  }[];
  /** Andere leveranciers, laatste 12 maanden; alleen met Adresboek *Bewerken* en zonder gebouwfilter. */
  compare: { company: { id: string; name: string; trade: string | null }; metrics: InsightMetricsDto }[] | null;
}
